/**
 * TenantSwitcher — botón al fondo del sidebar con el tenant activo.
 *
 * - Muestra avatar de iniciales (2 letras) + display_name + el estado de la
 *   organización (Beta con sus días, Activa, Solo lectura, Suspendida;
 *   OT-0073 §4.4), con la misma etiqueta que usa la consola de plataforma.
 * - Si el user está en limbo (sin tenant), muestra placeholder + hint.
 * - Dropdown para multi-tenant llega en B.5.d cuando exista
 *   `GET /me/tenants` (un user con varios tenants). En B.5.c.1 es estático.
 */

import { useTranslation } from 'react-i18next'

import type { MeResponse, TenantSummary } from '../../api/me'
import { formatDay, organizationState } from '../../lib/organizationState'

interface TenantSwitcherProps {
  me: MeResponse | undefined
}

export function TenantSwitcher({ me }: TenantSwitcherProps) {
  const { t } = useTranslation('shell')

  const tenant = me?.tenant
  const initials = tenant
    ? buildInitials(tenant.display_name)
    : '··'

  return (
    <button
      type="button"
      disabled
      className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left disabled:cursor-default"
      aria-label={t('tenant_switcher.active_label')}
    >
      <div className="w-7 h-7 rounded-md bg-brand-soft text-brand grid place-items-center text-xs font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate text-ink">
          {tenant?.display_name ?? t('tenant_switcher.no_tenant')}
        </p>
        <p className="text-[10px] text-ink-subtle">
          {tenant ? <OrganizationStateLabel tenant={tenant} /> : t('tenant_switcher.no_tenant_hint')}
        </p>
      </div>
    </button>
  )
}

function OrganizationStateLabel({ tenant }: { tenant: TenantSummary }) {
  const { t, i18n } = useTranslation('shell')
  const state = organizationState(tenant)
  return (
    <>
      {state === 'beta' && tenant.trial_ends_at
        ? t('organization.beta_read_only_from', {
            date: formatDay(tenant.trial_ends_at, i18n.language),
          })
        : t(`organization.state.${state}`)}
    </>
  )
}

/** Construye iniciales de 2 letras desde un nombre. "Kernel Demo" → "KD". */
function buildInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '··'
  if (parts.length === 1) {
    const word = parts[0] ?? ''
    return word.slice(0, 2).toUpperCase()
  }
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase()
}
